import { toNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import Tooltip from 'dashboard/components/Tooltip';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import AIPromptLivePreviewPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/AIPromptLivePreviewPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { ExternalIntegrationProductivityOpenAi } from 'dashboard/models/external-integration';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionGPTGenerateMessageV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Slider from 'design-system/components/Slider';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { JsonSchemaStringifiableTypes } from 'lib/fields';
import useExternalIntegration from 'lib/hooks/useExternalIntegration';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const CHAR_TO_TOKEN_ESTIMATE = 4;

export enum GptModel {
  GPT4O = 'gpt-4o',
  GPT4TURBO = 'gpt-4-turbo',
  GPT4 = 'gpt-4',
  GPT40613 = 'gpt-4-0613',
  GPT35TURBO = 'gpt-3.5-turbo',
  GPT35TURBO16K = 'gpt-3.5-turbo-16k',
  GPT35TURBO16K0613 = 'gpt-3.5-turbo-16k-0613',
}

const ALL_MODEL_TYPES: GptModel[] = [
  GptModel.GPT4O,
  GptModel.GPT4TURBO,
  GptModel.GPT4,
  GptModel.GPT40613,
  GptModel.GPT35TURBO,
  GptModel.GPT35TURBO16K,
  GptModel.GPT35TURBO16K0613,
];

const MAX_TOKENS_ALLOWED_NUM: Record<string, number> = {
  [GptModel.GPT4O]: 128_000,
  [GptModel.GPT4TURBO]: 128_000,
  [GptModel.GPT4]: 8180,
  [GptModel.GPT40613]: 8180,
  [GptModel.GPT35TURBO]: 4080,
  [GptModel.GPT35TURBO16K]: 16350,
  [GptModel.GPT35TURBO16K0613]: 4070,
};

const StyledParagraph = styled(Paragraph)`
  width: 350px;
`;

interface ParamsGPTGenerateMessageV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionGPTGenerateMessageV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionGPTGenerateMessageV1['params']) => void;
}

const REACT_SELECT_PORTAL_ID_PREFIX = 'params-gpt-generate-message';

const ParamsGPTGenerateContentV1: React.VFC<ParamsGPTGenerateMessageV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const [tokenNum, setTokenNum] = useState(params['max-tokens']);

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const [openaiExternalIntegrations, { isLoading: isLoading }] = useExternalIntegration([
    ApiModel.ExternalIntegrationProductivityOpenAi,
  ]);

  const openaiInstanceOptions = [
    ...openaiExternalIntegrations.map((integration) => ({
      label: integration.attributes.name,
      value: integration.id,
    })),
    { label: 'Add New OpenAI API key', value: 'marketplace-managment-redirect' },
  ];

  const navigate = useNavigate();
  const selectExtIntInstance = (extIntInstance: string) => {
    if (extIntInstance === 'marketplace-managment-redirect') {
      navigate(`/dashboard/marketplace/openai`);
    } else {
      updateActionParams({
        ...params,
        'external-integration-id': extIntInstance,
      });
    }
  };

  const updateGPTParams = <Key extends keyof WorkflowActionGPTGenerateMessageV1['params']>(
    key: Key,
    value: WorkflowActionGPTGenerateMessageV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (params['max-tokens'] > MAX_TOKENS_ALLOWED_NUM[params['gpt-model']]) {
      setTokenNum(MAX_TOKENS_ALLOWED_NUM[params['gpt-model']]);
      updateGPTParams('max-tokens', MAX_TOKENS_ALLOWED_NUM[params['gpt-model']]);
    }
  }, [params['gpt-model']]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionGPTGenerateMessageV1['params']>(errors);

  const [showPreviewPanel, { disable: closePreviewPanel, enable: enablePreviewPanel }] =
    useToggle(false);

  return (
    <div id={`${REACT_SELECT_PORTAL_ID_PREFIX}__panel`}>
      <Spacer height={2} />
      <ParamLabel
        labelText='OpenAI API Key'
        htmlFor='params-gpt-generate-message__external-integration-id'
      />
      <Select
        value={params['external-integration-id']}
        onValueChange={selectExtIntInstance}
        id='params-gpt-generate-message__external-integration-id'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('external-integration-id')}
      >
        {openaiInstanceOptions.map((opt) => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      <Spacer height={2} />
      <ParamLabel labelText='GPT Model' htmlFor='params-gpt-generate-message__gpt-model' />
      <Select
        value={params['gpt-model']}
        onValueChange={(value) => updateGPTParams('gpt-model', value)}
        id='params-gpt-generate-message__gpt-model'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('gpt-model')}
      >
        {ALL_MODEL_TYPES.map((model) => (
          <Option key={model} value={model}>
            {model}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('gpt-model')}
      <Spacer height={2} />
      <div
        id='params-gpt-generate-message__prompt-clickable'
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick={params['external-integration-id'] ? enablePreviewPanel : null}
      >
        <ParamLabel labelText='Prompt' htmlFor='params-gpt-generate-message__prompt' />
        <HandlebarFormControl
          type='text-area'
          value={params['prompt']}
          updateValue={(value) => updateGPTParams('prompt', value)}
          editable={editable && !isLoading && !showPreviewPanel}
          allowedTypes={JsonSchemaStringifiableTypes}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          id='params-gpt-generate-message__prompt'
          hasError={hasErrorFor('prompt')}
        />
        {renderErrorMessageFor('prompt')}
      </div>
      <Spacer height={2} />
      <Tooltip
        trigger='hover'
        tooltip={
          <StyledParagraph color='primary' size='md'>
            The maximum number of tokens to generate shared between the prompt and completion. The
            exact limit varies by model. (One token is roughly 4 characters for standard English
            text)
          </StyledParagraph>
        }
        hideArrow={true}
      >
        <ParamLabel labelText='Max Length' htmlFor='params-gpt-generate-message__max-tokens' />
      </Tooltip>
      <Slider
        value={tokenNum}
        max={
          MAX_TOKENS_ALLOWED_NUM[params['gpt-model']] -
          params['prompt'].length / CHAR_TO_TOKEN_ESTIMATE
        }
        min={1}
        step={1}
        disabled={!editable}
        track={true}
        width='auto'
        id='params-gpt-generate-message__max-tokens'
        onChange={(value) => setTokenNum(toNumber(value))}
        onBlur={() => {
          updateGPTParams('max-tokens', tokenNum);
        }}
      />
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <Tooltip
          trigger='hover'
          tooltip={
            <StyledParagraph color='primary' size='md'>
              Controls randomness: Lowering results in less random completions. As the temperature
              approaches zero, the model will become deterministic and repetitive.
            </StyledParagraph>
          }
          hideArrow={true}
        >
          <ParamLabel labelText='Temperature' htmlFor='params-gpt-generate-message__temperature' />
        </Tooltip>
        <Slider
          defaultValue={params['temperature']}
          max={2}
          min={0}
          disabled={!editable}
          step={0.01}
          width='auto'
          id='params-gpt-generate-message__temperature'
          onBlur={(e) => {
            updateGPTParams('temperature', toNumber((e.target as HTMLInputElement).value));
          }}
        />
        <Spacer height={2} />
        <Tooltip
          trigger='hover'
          tooltip={
            <StyledParagraph color='primary' size='md'>
              Controls diversity via nucleus sampling: 0.5 means half of all likelihood weighted
              options are considered.
            </StyledParagraph>
          }
          hideArrow={true}
        >
          <ParamLabel labelText='Top-P' htmlFor='params-gpt-generate-message__top-p' />
        </Tooltip>
        <Slider
          defaultValue={params['top-p']}
          max={1}
          min={0}
          step={0.01}
          disabled={!editable}
          id='params-gpt-generate-content__top-p'
          width='auto'
          onBlur={(e) => {
            updateGPTParams('top-p', toNumber((e.target as HTMLInputElement).value));
          }}
        />
        <Spacer height={2} />
        <Tooltip
          trigger='hover'
          tooltip={
            <StyledParagraph color='primary' size='md'>
              How much to penalize new tokens based on their exisiting frequency in the text so far.
              Decreases the model&apos;s likelihood to repeate the same line verbatim.
            </StyledParagraph>
          }
          hideArrow={true}
        >
          <ParamLabel
            labelText='Frequency Penalty'
            htmlFor='params-gpt-generate-message__frequency_penalty'
          />
        </Tooltip>
        <Slider
          defaultValue={params['frequency-penalty']}
          max={2}
          min={0}
          step={0.01}
          width='auto'
          disabled={!editable}
          id='params-gpt-generate-message__frequency_penalty'
          onBlur={(e) => {
            updateGPTParams('frequency-penalty', toNumber((e.target as HTMLInputElement).value));
          }}
        />
        <Spacer height={2} />
        <Tooltip
          trigger='hover'
          tooltip={
            <StyledParagraph color='primary' size='md'>
              How much to penalize new tokens based on whether they appear in the text so far.
              Increases the model&apos;s likelihood to talk about new topics.
            </StyledParagraph>
          }
          hideArrow={true}
        >
          <ParamLabel
            labelText='Presence Penalty'
            htmlFor='params-gpt-generate-content__presence_penalty'
          />
        </Tooltip>
        <Slider
          defaultValue={params['presence-penalty']}
          max={2}
          min={0}
          step={0.01}
          width='auto'
          disabled={!editable}
          id='params-gpt-generate-content__presence_penalty'
          onBlur={(e) => {
            updateGPTParams('presence-penalty', toNumber((e.target as HTMLInputElement).value));
          }}
        />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
      <Spacer height={2} />
      {showPreviewPanel && (
        <AIPromptLivePreviewPanel
          title='OpenAI GPT Generate Message Preview'
          externalIntegration={
            openaiExternalIntegrations.find(
              (ext) => ext.id === params['external-integration-id'],
            ) as ExternalIntegrationProductivityOpenAi
          }
          params={params}
          editable={editable}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          onOutsideClick={closePreviewPanel}
          onUpdatePrompt={(value) => updateGPTParams('prompt', value)}
          idPortalPrefix={REACT_SELECT_PORTAL_ID_PREFIX}
        />
      )}
    </div>
  );
};

export default ParamsGPTGenerateContentV1;
